import React, { useEffect, useRef, useMemo, useCallback, useState } from "react";

import { createPortal } from "react-dom";

import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { changeLanguageWithReload } from "../i18n";

import { useTheme } from "../contexts/ThemeContext";
import { usePageTour } from "../contexts/PageTourContext";
import { useBrandLogo } from "../utils/brandLogo";
import {
  getAllEWalletsAmount,
  getClientNotifications,
  getMarketingPlanAttachment,
  getMarketingPlans,
} from "../utils/apiClient";
import AppConstants from "../utils/appConstants";

import "./css/AppTopHeader.css";



export default function AppTopHeader({

  user = null,

  profileUrl = null,

  isCollapsed = false,

  isMobile = false,

  onToggleSidebar,

  onCollapseSidebar,

  sidebarSearchItems = [],

  onLogout,

}) {
  const STORY_SEEN_STORAGE_PREFIX = "mfg:stories:seen";

  const { t, i18n } = useTranslation("mobile");

  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { startTour } = usePageTour();
  const brandLogo = useBrandLogo();

  const searchRef = useRef(null);

  const searchWrapRef = useRef(null);
  const userMenuRef = useRef(null);
  const notificationsWrapRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");

  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState(null);
  const [notifItems, setNotifItems] = useState([]);
  const [badgeCount, setBadgeCount] = useState(0);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const [activeStoryIndex, setActiveStoryIndex] = useState(0);
  const [stories, setStories] = useState([]);
  const currentStory = stories[activeStoryIndex] || null;

  const hasStories = stories.length > 0;
  const storyFingerprint = useMemo(
    () => stories.map((story) => story.id).join("|"),
    [stories]
  );
  const getTodayDateToken = useCallback(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);
  const getStorySeenStorageKey = useCallback(() => {
    const userKey =
      user?.USER_ID ||
      user?.user_information?.EMAIL ||
      user?.userInformation?.EMAIL ||
      user?.EMAIL ||
      sessionStorage.getItem("userEmail") ||
      localStorage.getItem("userEmail") ||
      "guest";
    return `${STORY_SEEN_STORAGE_PREFIX}:${String(userKey)}:${storyFingerprint}`;
  }, [user, storyFingerprint]);
  const [hasUnseenStories, setHasUnseenStories] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const blobUrls = [];

    const inferMediaType = (fileName, mimeType) => {
      const lowerName = String(fileName || "").toLowerCase();
      const lowerMime = String(mimeType || "").toLowerCase();
      if (lowerMime.startsWith("video/")) return "video";
      if (lowerMime.startsWith("image/")) return "image";
      if (/\.(mp4|webm|ogg|mov|m4v)$/i.test(lowerName)) return "video";
      return "image";
    };

    const tryLoadMarketingAttachment = async (fileName) => {
      try {
        const response = await getMarketingPlanAttachment(fileName);
        const blob = response?.data;
        if (!blob || !(blob instanceof Blob)) return null;
        const ct = String(blob.type || "").toLowerCase();
        if (ct.includes("application/json") || ct.includes("text/html")) {
          return null;
        }
        const blobUrl = URL.createObjectURL(blob);
        blobUrls.push(blobUrl);
        return {
          url: blobUrl,
          mediaType: inferMediaType(fileName, blob?.type),
        };
      } catch (_) {
        return null;
      }
    };

    const loadStoriesFromMarketingPlans = async () => {
      try {
        const response = await getMarketingPlans();
        const payload = response?.decryptedData || response?.data || {};
        if (Number(payload?.CODE) !== AppConstants.responseOk) {
          if (!cancelled) setStories([]);
          return;
        }

        const rows = Array.isArray(payload?.DATA) ? payload.DATA : [];
        const storiesFromApi = [];
        for (const row of rows) {
          const attachmentName = String(row?.ATTACHMENT || "").trim();
          if (!attachmentName) continue;

          const loadedAttachment = await tryLoadMarketingAttachment(attachmentName);
          if (!loadedAttachment?.url) continue;

          storiesFromApi.push({
            id: `marketing-story-${row?.ID || attachmentName}`,
            mediaType: loadedAttachment.mediaType || "image",
            source: loadedAttachment.url,
            fileName: attachmentName,
            alt:
              row?.marketing_configuration?.TITLE ||
              row?.marketingConfiguration?.TITLE ||
              "Marketing story",
          });
        }

        if (!cancelled) {
          setStories(storiesFromApi);
          setActiveStoryIndex(0);
        }
      } catch (_) {
        if (!cancelled) setStories([]);
      }
    };

    loadStoriesFromMarketingPlans();

    return () => {
      cancelled = true;
      blobUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {}
      });
    };
  }, []);

  useEffect(() => {
    if (!hasStories) {
      setHasUnseenStories(false);
      return;
    }
    let seenStoriesToday = false;
    try {
      const storageKey = getStorySeenStorageKey();
      const todayToken = getTodayDateToken();
      const storedDate =
        localStorage.getItem(storageKey) || sessionStorage.getItem(storageKey) || "";
      seenStoriesToday = storedDate === todayToken;
    } catch (_) {
      seenStoriesToday = false;
    }
    setHasUnseenStories(!seenStoriesToday);
  }, [hasStories, getStorySeenStorageKey, getTodayDateToken]);

  const userDisplayInitial = useMemo(() => {
    const raw =
      user?.user_information?.FULL_NAME ||
      user?.userInformation?.FULL_NAME ||
      user?.NICK_NAME ||
      user?.USER_NAME ||
      user?.user_information?.EMAIL ||
      user?.userInformation?.EMAIL ||
      (typeof window !== "undefined"
        ? sessionStorage.getItem("userEmail") || localStorage.getItem("userEmail")
        : "") ||
      "";
    const s = String(raw).trim();
    if (!s) return "?";
    const first = [...s][0];
    if (!first) return "?";
    try {
      return first.toLocaleUpperCase(i18n.language || undefined);
    } catch (_) {
      return first.toUpperCase();
    }
  }, [user, i18n.language]);

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [profileUrl]);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const searchResults = useMemo(() => {

    if (!normalizedSearch || !Array.isArray(sidebarSearchItems) || sidebarSearchItems.length === 0) {

      return [];

    }

    return sidebarSearchItems

      .filter((item) => item?.label && item?.path && item.label.toLowerCase().includes(normalizedSearch))

      .slice(0, 10);

  }, [normalizedSearch, sidebarSearchItems]);



  const modKeyLabel = useMemo(() => {

    if (typeof navigator === "undefined") return "Ctrl+";

    return /Mac|iPhone|iPod|iPad/i.test(navigator.platform || navigator.userAgent || "")

      ? "⌘"

      : "Ctrl+";

  }, []);



  const focusSearch = useCallback(() => {

    searchRef.current?.focus?.();

  }, []);



  useEffect(() => {

    const onKey = (e) => {

      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {

        e.preventDefault();

        focusSearch();

      }

    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);

  }, [focusSearch]);

  useEffect(() => {

    const onDocMouseDown = (e) => {

      if (!searchWrapRef.current || !searchOpen) return;

      if (!searchWrapRef.current.contains(e.target)) {

        setSearchOpen(false);

      }

    };

    document.addEventListener("mousedown", onDocMouseDown);

    return () => document.removeEventListener("mousedown", onDocMouseDown);

  }, [searchOpen]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!userMenuRef.current || !userMenuOpen) return;
      if (!userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [userMenuOpen]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!notificationsWrapRef.current || !notifOpen) return;
      if (!notificationsWrapRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [notifOpen]);

  useEffect(() => {
    const onBadge = (e) => {
      const n = Number(e?.detail?.count);
      if (!Number.isFinite(n) || n < 0) return;
      setBadgeCount(n);
    };
    // Live bump when FCM / notification arrives (same idea as chat unread ++)
    const onHeaderPreview = () => {
      setBadgeCount((c) => (Number(c) || 0) + 1);
    };
    window.addEventListener("mfg:notification-badge", onBadge);
    window.addEventListener("mfg:header-notification", onHeaderPreview);
    return () => {
      window.removeEventListener("mfg:notification-badge", onBadge);
      window.removeEventListener("mfg:header-notification", onHeaderPreview);
    };
  }, []);

  // Initial + refresh badge count from existing wallet/home NOTIFICATIONS payload (no API changes)
  useEffect(() => {
    if (!user) {
      setBadgeCount(0);
      setNotifOpen(false);
      setNotifItems([]);
      setNotifError(null);
      return undefined;
    }

    let cancelled = false;
    const syncBadge = async () => {
      try {
        const res = await getAllEWalletsAmount();
        const data = res?.decryptedData || res?.data || {};
        if (cancelled) return;
        if (Number(data?.CODE) !== AppConstants.responseOk && Number(data?.CODE) !== 200) return;
        const count = Math.max(0, Number(data?.NOTIFICATIONS?.NOTIFICATIONS) || 0);
        setBadgeCount(count);
        try {
          window.dispatchEvent(
            new CustomEvent("mfg:notification-badge", { detail: { count } })
          );
        } catch (_) {}
      } catch (_) {}
    };

    syncBadge();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setStoryOpen(false);
      }
    };
    if (storyOpen) {
      window.addEventListener("keydown", onEsc);
    }
    return () => window.removeEventListener("keydown", onEsc);
  }, [storyOpen]);

  useEffect(() => {
    if (!storyOpen || !hasStories) return undefined;
    const timerId = window.setInterval(() => {
      setActiveStoryIndex((prev) => {
        if (prev >= stories.length - 1) {
          setStoryOpen(false);
          return 0;
        }
        return prev + 1;
      });
    }, 15000);
    return () => window.clearInterval(timerId);
  }, [storyOpen, hasStories, stories.length]);

  const mapRowToNotificationItem = useCallback((row) => {
    const msg = row?.notifications_message || row?.NOTIFICATIONS_MESSAGE;
    let title = "—";
    let body = "";
    if (msg?.MESSAGE != null) {
      try {
        const raw = msg.MESSAGE;
        const j = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (j && typeof j === "object") {
          if (j.title != null && String(j.title).trim() !== "") title = String(j.title);
          if (j.body != null) body = String(j.body);
        }
      } catch (_) {}
    }
    const id = row?.ID != null ? String(row.ID) : `${title}-${msg?.UPDATED_AT || ""}`;
    const at = row?.LAST_SEEN || msg?.UPDATED_AT || null;
    return { id, title, body, at };
  }, []);

  const formatNotificationTime = useCallback((value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const loadHeaderNotifications = useCallback(async () => {
    setNotifLoading(true);
    setNotifError(null);
    try {
      const res = await getClientNotifications(15);
      const data = res?.decryptedData || res?.data;
      if (!data || data.CODE !== AppConstants.responseOk) {
        const msg = data?.MESSAGE || data?.message || t("no_data_found");
        setNotifError(typeof msg === "string" ? msg : t("no_data_found"));
        setNotifItems([]);
        return;
      }
      const page = data.DATA;
      const rows = page?.data ?? page?.DATA ?? [];
      const list = Array.isArray(rows) ? rows.map(mapRowToNotificationItem) : [];
      setNotifItems(list);
      setBadgeCount(0);
    } catch (err) {
      setNotifError(err?.message || t("no_data_found"));
      setNotifItems([]);
    } finally {
      setNotifLoading(false);
    }
  }, [mapRowToNotificationItem, t]);

  const onProfileClick = () => {

    navigate("/pages/profile-information");
    setUserMenuOpen(false);

    if (isMobile && !isCollapsed && typeof onToggleSidebar === "function") {

      onToggleSidebar();

    }

  };



  const onNotificationsClick = () => {
    setUserMenuOpen(false);
    setNotifOpen((prev) => {
      const next = !prev;
      if (next) {
        loadHeaderNotifications();
      }
      return next;
    });
  };

  const onLanguageClick = (langCode) => {
    if (!langCode) return;
    setUserMenuOpen(false);
    if (isMobile && !isCollapsed && typeof onToggleSidebar === "function") {
      onToggleSidebar();
    }
    changeLanguageWithReload(langCode);
  };

  const onLogoutClick = () => {
    setUserMenuOpen(false);
    if (isMobile && !isCollapsed && typeof onToggleSidebar === "function") {
      onToggleSidebar();
    }
    if (typeof onLogout === "function") {
      onLogout();
    }
  };

  const openStories = useCallback(() => {
    if (!hasStories) return;
    try {
      const storageKey = getStorySeenStorageKey();
      const todayToken = getTodayDateToken();
      // Persist in localStorage so logout/login on the same day keeps stories marked as seen.
      localStorage.setItem(storageKey, todayToken);
      sessionStorage.setItem(storageKey, todayToken);
    } catch (_) {}
    setHasUnseenStories(false);
    setActiveStoryIndex(0);
    setStoryOpen(true);
  }, [hasStories, getStorySeenStorageKey, getTodayDateToken]);

  const goToPrevStory = useCallback(() => {
    setActiveStoryIndex((prev) => (prev <= 0 ? stories.length - 1 : prev - 1));
  }, [stories.length]);

  const goToNextStory = useCallback(() => {
    setActiveStoryIndex((prev) => {
      if (prev >= stories.length - 1) {
        setStoryOpen(false);
        return 0;
      }
      return prev + 1;
    });
  }, [stories.length]);



  const menuLabel =

    isMobile && !isCollapsed ? t("app_header_close_menu") : t("app_header_open_menu");



  const handleCollapse = () => {

    if (typeof onCollapseSidebar === "function") {

      onCollapseSidebar();

    }

  };

  const goToSearchResult = useCallback(

    (path) => {

      if (!path) return;

      navigate(path);

      setSearchQuery("");

      setSearchOpen(false);

      if (isMobile && !isCollapsed && typeof onToggleSidebar === "function") {

        onToggleSidebar();

      }

    },

    [navigate, isMobile, isCollapsed, onToggleSidebar]

  );

  return createPortal(

    <header className="app-top-header" role="banner">

      <div className="app-top-header-inner">

        <div className="app-top-header-sidebar-zone">

          {isCollapsed ? (

            <button

              type="button"

              className="app-top-header-open-menu"

              aria-label={menuLabel}

              onClick={onToggleSidebar}

            >

              <i className="fa-solid fa-bars" aria-hidden />

            </button>

          ) : (

            <>
              <div className="app-top-header-brand-row">

                <button
                  type="button"
                  className={`app-top-header-logo-wrap ${hasUnseenStories ? "app-top-header-logo-wrap--has-story" : ""}`}
                  onClick={openStories}
                  aria-label="Open daily stories"
                >
                  <img src={brandLogo} alt="MFG Invest" className="app-top-header-mfg-logo" />
                </button>

                <span className="app-top-header-brand-name-inline">{t("app_name")}</span>

              </div>

              <button

                type="button"

                className="app-top-header-collapse-btn"

                aria-label={t("sidebar_collapse")}

                onClick={handleCollapse}

              >

                <i className="fa-solid fa-angles-left" aria-hidden />

              </button>

            </>

          )}

        </div>



        <div className="app-top-header-main-zone">

          <div className="app-top-header-search-wrap" ref={searchWrapRef}>

            <label className="app-top-header-search" htmlFor="app-top-header-search-input">

              <i className="fa-solid fa-magnifying-glass app-top-header-search-icon" aria-hidden />

              <input

                id="app-top-header-search-input"

                ref={searchRef}

                type="search"

                className="app-top-header-search-input"

                placeholder={t("app_header_search_placeholder")}

                autoComplete="off"

                value={searchQuery}

                onChange={(e) => {

                  setSearchQuery(e.target.value);

                  setSearchOpen(true);

                }}

                onFocus={() => setSearchOpen(true)}

                role="combobox"

                aria-expanded={searchOpen && searchResults.length > 0}

                aria-controls="app-top-header-search-listbox"

                aria-autocomplete="list"

                onKeyDown={(e) => {

                  if (e.key === "Escape") {

                    setSearchQuery("");

                    setSearchOpen(false);

                    e.target.blur();

                    return;

                  }

                  if (e.key === "Enter" && searchResults.length > 0) {

                    e.preventDefault();

                    goToSearchResult(searchResults[0].path);

                  }

                }}

              />

              <span className="app-top-header-kbd" title={`${modKeyLabel}K`}>

                <kbd className="app-top-header-kbd-key">{modKeyLabel}</kbd>

                <kbd className="app-top-header-kbd-key">K</kbd>

              </span>

            </label>

            {searchOpen && normalizedSearch.length > 0 && searchResults.length > 0 && (

              <ul

                id="app-top-header-search-listbox"

                className="app-top-header-search-results"

                role="listbox"

              >

                {searchResults.map((item, idx) => (

                  <li key={`${item.path}-${item.label}-${idx}`} role="option">

                    <button

                      type="button"

                      className="app-top-header-search-result-btn"

                      onMouseDown={(ev) => ev.preventDefault()}

                      onClick={() => goToSearchResult(item.path)}

                    >

                      {item.label}

                    </button>

                  </li>

                ))}

              </ul>

            )}

          </div>



          <div className="app-top-header-right">

            <div className="app-top-header-notifications-wrap" ref={notificationsWrapRef}>
              <button
                type="button"
                className="app-top-header-icon-btn app-top-header-notifications"
                aria-label={t("app_header_notifications")}
                aria-expanded={notifOpen}
                aria-haspopup="menu"
                onClick={onNotificationsClick}
              >
                <i className="fa-regular fa-bell" />
                {badgeCount > 0 && (
                  <span className="app-top-header-notifications-badge" aria-hidden>
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="app-top-header-notifications-dropdown" aria-label={t("app_header_notifications")}>
                  {notifLoading && (
                    <div className="app-top-header-notifications-status">{t("wait_a_moment")}</div>
                  )}
                  {!notifLoading && notifError && (
                    <div className="app-top-header-notifications-status app-top-header-notifications-status--error">
                      {notifError}
                    </div>
                  )}
                  {!notifLoading && !notifError && notifItems.length === 0 && (
                    <div className="app-top-header-notifications-status">{t("no_data_found")}</div>
                  )}
                  {!notifLoading && !notifError && notifItems.length > 0 && (
                    <ul className="app-top-header-notifications-list">
                      {notifItems.map((item) => (
                        <li key={item.id} className="app-top-header-notifications-item">
                          <span className="app-top-header-notifications-item-title">{item.title}</span>
                          {item.at ? (
                            <span className="app-top-header-notifications-item-time">
                              {formatNotificationTime(item.at)}
                            </span>
                          ) : null}
                          {item.body ? (
                            <span className="app-top-header-notifications-item-body">{item.body}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="app-top-header-user-menu-wrap" ref={userMenuRef}>
            <button
              type="button"
              className="app-top-header-user"
              onClick={() => {
                setNotifOpen(false);
                setUserMenuOpen((prev) => !prev);
              }}
              aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label={t("profile", { ns: "mobile" })}
              >
                <span className="app-top-header-avatar-wrap">
                  {profileUrl && !avatarLoadFailed ? (
                    <img
                      src={profileUrl}
                      alt=""
                      className="app-top-header-avatar"
                      onError={() => setAvatarLoadFailed(true)}
                    />
                  ) : (
                    <span className="app-top-header-avatar-initial" aria-hidden>
                      {userDisplayInitial}
                    </span>
                  )}
                </span>
              </button>
              {userMenuOpen && (
                <div className="app-top-header-user-dropdown" role="menu">
                  <button
                    type="button"
                    className="app-top-header-user-dropdown-item"
                    onClick={onProfileClick}
                    role="menuitem"
                  >
                    {t("profile", { ns: "mobile" })}
                  </button>
                  <div
                    className="app-top-header-user-dropdown-group"
                    role="group"
                    aria-label={t("language", { ns: "mobile" })}
                  >
                    <span className="app-top-header-user-dropdown-label">
                      {t("language", { ns: "mobile" })}
                    </span>
                    <div className="app-top-header-user-dropdown-inline">
                      <button
                        type="button"
                        className="app-top-header-user-dropdown-chip"
                        onClick={() => onLanguageClick("en")}
                      >
                        {t("en", { ns: "mobile" }) || "EN"}
                      </button>
                      <button
                        type="button"
                        className="app-top-header-user-dropdown-chip"
                        onClick={() => onLanguageClick("ar")}
                      >
                        {t("ar", { ns: "mobile" }) || "AR"}
                      </button>
                      <button
                        type="button"
                        className="app-top-header-user-dropdown-chip"
                        onClick={() => onLanguageClick("fr")}
                      >
                        {t("fr", { ns: "mobile" }) || "FR"}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="app-top-header-user-dropdown-item"
                    onClick={() => {
                      toggleTheme();
                      setUserMenuOpen(false);
                    }}
                    role="menuitem"
                  >
                    {isDarkMode ? t("light_mode", { ns: "mobile" }) : t("dark_mode", { ns: "mobile" })}
                  </button>
                  <button
                    type="button"
                    className="app-top-header-user-dropdown-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      startTour();
                    }}
                    role="menuitem"
                  >
                    {t("how_it_works", { ns: "mobile" })}
                  </button>
                  <button
                    type="button"
                    className="app-top-header-user-dropdown-item"
                    onClick={onLogoutClick}
                    role="menuitem"
                  >
                    {t("logout", { ns: "mobile" })}
                  </button>
                </div>
              )}
            </div>

          </div>

        </div>

      </div>

      {storyOpen && hasStories && (
        <div className="app-top-header-story-overlay" role="dialog" aria-modal="true" aria-label="Daily stories">
          <button
            type="button"
            className="app-top-header-story-backdrop"
            aria-label="Close stories"
            onClick={() => setStoryOpen(false)}
          />
          <div className="app-top-header-story-modal">
            <button
              type="button"
              className="app-top-header-story-close"
              aria-label="Close stories"
              onClick={() => setStoryOpen(false)}
            >
              <i className="fa-solid fa-xmark" aria-hidden />
            </button>
            <div
              className="app-top-header-story-progress"
              style={{ "--story-count": Math.max(stories.length, 1) }}
              aria-hidden
            >
              {stories.map((story, index) => (
                <span
                  key={story.id}
                  className={`app-top-header-story-progress-item ${
                    index < activeStoryIndex ? "app-top-header-story-progress-item--done" : ""
                  }`}
                >
                  {index === activeStoryIndex && (
                    <span
                      key={`${story.id}-${activeStoryIndex}`}
                      className="app-top-header-story-progress-item-fill app-top-header-story-progress-item-fill--active"
                    />
                  )}
                </span>
              ))}
            </div>
            <div className="app-top-header-story-body">
              <button
                type="button"
                className="app-top-header-story-nav app-top-header-story-nav--prev"
                aria-label="Previous story"
                onClick={goToPrevStory}
              >
                <i className="fa-solid fa-chevron-left" aria-hidden />
              </button>
              {currentStory?.mediaType === "video" ? (
                <video
                  src={currentStory?.source}
                  className="app-top-header-story-image"
                  controls
                  playsInline
                  autoPlay
                />
              ) : (
                <img
                  src={currentStory?.source}
                  alt={currentStory?.alt || "Daily story"}
                  className="app-top-header-story-image"
                />
              )}
              <button
                type="button"
                className="app-top-header-story-nav app-top-header-story-nav--next"
                aria-label="Next story"
                onClick={goToNextStory}
              >
                <i className="fa-solid fa-chevron-right" aria-hidden />
              </button>
            </div>
            <div className="app-top-header-story-footer">
              <a
                href={currentStory?.source || "#"}
                download={currentStory?.fileName || `story-${activeStoryIndex + 1}`}
                className="app-top-header-story-download"
                aria-label={
                  currentStory?.mediaType === "video" ? "Download story video" : "Download story image"
                }
                title="Download"
              >
                <i className="fa-solid fa-download" aria-hidden />
              </a>
            </div>
          </div>
        </div>
      )}

    </header>,

    document.body

  );

}


